import styles from "./Toolbar.module.css"

import type { LinkDialogOrigin } from "@shared/unified-tiptap/extensions/link/link-inline-extension"
import type { Editor } from "@tiptap/core"
import { EditorContext } from "@tiptap/react"
import { useEffect, useRef } from "react"
import { UNSAFE_PortalProvider } from "react-aria"
import { Toolbar as AriaToolbar } from "react-aria-components"
import { ColorPopover } from "./ColorPopover"
import { ExtraDropdownMenu } from "./ExtraDropdownMenu"
import { HeadingDropdownMenu } from "./HeadingDropdownMenu"
import { HighlightPopover } from "./HighlightPopover"
import { LinkButton } from "./LinkButton"
import { ListDropdownMenu } from "./ListDropdownMenu"
import { MarkButton } from "./MarkButton"
import { UnderlinePopover } from "./UnderlinePopover"

export function Toolbar({
  editor,
  openLinkDialog,
  onDismiss,
}: {
  editor: Editor
  openLinkDialog: (origin: LinkDialogOrigin) => void
  /** Escape pressed while the toolbar contains focus — dismiss it (see FormattingToolbar). */
  onDismiss: () => void
}) {
  const toolbarPortalRef = useRef<HTMLDivElement | null>(null)
  const toolbarRootRef = useRef<HTMLDivElement | null>(null)

  // Escape from inside the toolbar dismisses it (the selection stays — it re-arms on
  // the next selection change; see FormattingToolbar) and returns focus to the editor.
  // Document capture, because react-aria's TooltipTrigger (on every toolbar button)
  // swallows Escape with a document-capture stopPropagation while its tooltip is open
  // — only a same-node listener still runs. Focus inside the toolbar's portal is
  // skipped: an open dropdown/popover there owns its Escape (dismissal), and focus
  // stays with it.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return
      if (!toolbarRootRef.current?.contains(document.activeElement)) return
      onDismiss()
      editor.commands.focus()
    }
    document.addEventListener("keydown", onKeyDown, true)
    return () => document.removeEventListener("keydown", onKeyDown, true)
  }, [editor, onDismiss])

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Forward modifier shortcuts (Cmd/Ctrl) to the editor so they work while toolbar has focus.
    if (e.metaKey || e.ctrlKey) {
      const forwarded = new KeyboardEvent("keydown", {
        key: e.key,
        code: e.nativeEvent.code,
        ctrlKey: e.ctrlKey,
        metaKey: e.metaKey,
        shiftKey: e.shiftKey,
        altKey: e.altKey,
        bubbles: true,
        cancelable: true,
      })
      editor.view.dom.dispatchEvent(forwarded)
      if (forwarded.defaultPrevented) {
        e.preventDefault()
      }
    }
  }

  return (
    <EditorContext.Provider value={{ editor }}>
      {/* Portal dropdowns etc. so they stay within the toolbar's DOM tree.
        Needed so the toolbar counts as containing focus while interacting with them.
        Note: This will also portal modals, which is not intended but works okay. */}
      <UNSAFE_PortalProvider getContainer={() => toolbarPortalRef.current}>
        {/* The modifier forwarding lives on a plain div: react-aria filters unknown
            DOM props off its Toolbar. */}
        <div ref={toolbarRootRef} onKeyDown={handleKeyDown}>
          {/* react-aria Toolbar: role="toolbar", one tab stop, arrow-key navigation
              between the controls (reached via Alt+F10 — see FormattingToolbar). */}
          <AriaToolbar
            aria-label="Formatting"
            className={
              "flex flex-col gap-0 rounded-xl bg-primary p-1.5 shadow-xl ring-1 ring-gray-200"
            }
          >
            {editor.isEditable && (
              <>
                <div className={styles.buttonRow}>
                  <MarkButton editor={editor} type="bold" />
                  <MarkButton editor={editor} type="italic" />
                  <UnderlinePopover editor={editor} />
                  <HighlightPopover editor={editor} />
                  <ColorPopover editor={editor} />
                </div>

                <div className={styles.buttonRow}>
                  <LinkButton editor={editor} openLinkDialog={openLinkDialog} />
                  <MarkButton editor={editor} type="strike" />
                  <ListDropdownMenu editor={editor} />
                  <HeadingDropdownMenu editor={editor} />
                  <ExtraDropdownMenu editor={editor} />
                </div>
              </>
            )}
          </AriaToolbar>
        </div>
      </UNSAFE_PortalProvider>
      <div ref={toolbarPortalRef} className="fixed top-0 left-0 z-100" />
    </EditorContext.Provider>
  )
}
